import { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Megaphone } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { submitAdInquiry } from '@/lib/api';

const EMPTY_FORM = { name: '', email: '', company: '', message: '' };

export const AdvertiseModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in your name, email, and message');
      return;
    }
    setSubmitting(true);
    try {
      await submitAdInquiry(form);
      toast.success("Thanks! We'll be in touch soon.");
      setForm(EMPTY_FORM);
      onOpenChange(false);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111] border-[#222] text-white max-w-md" data-testid="advertise-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Megaphone className="text-yellow-500" size={20} />
            Advertise With Us
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            Tell us about your business and we'll get back to you.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ad-name" className="text-gray-400">Name</Label>
            <Input
              id="ad-name"
              value={form.name}
              onChange={handleChange('name')}
              className="bg-black border-[#333] text-white"
              data-testid="advertise-form-name"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ad-email" className="text-gray-400">Email</Label>
            <Input
              id="ad-email"
              type="email"
              value={form.email}
              onChange={handleChange('email')}
              className="bg-black border-[#333] text-white"
              data-testid="advertise-form-email"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ad-company" className="text-gray-400">Business / Company (optional)</Label>
            <Input
              id="ad-company"
              value={form.company}
              onChange={handleChange('company')}
              className="bg-black border-[#333] text-white"
              data-testid="advertise-form-company"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ad-message" className="text-gray-400">Message</Label>
            <Textarea
              id="ad-message"
              value={form.message}
              onChange={handleChange('message')}
              className="bg-black border-[#333] text-white min-h-[100px]"
              data-testid="advertise-form-message"
            />
          </div>

          <Button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-bold"
            data-testid="advertise-form-submit"
          >
            {submitting ? <Loader2 className="animate-spin" size={18} /> : 'Send Inquiry'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdvertiseModal;
